'use client';

import {useRef, useState, type KeyboardEvent} from 'react';

import Icon from '@/atoms/Icon/Icon';
import Logo from '@/atoms/Logo/Logo';
import NotificationWrapper from '@/atoms/Wrappers/Notification/NotificationWrapper';
import ProfileMenu from '@/molecules/Fragments/Menu/Profile/ProfileMenu';
import Search from '@/molecules/Search/Search';
import {getSearchAutocomplete} from '@/redux/actions/searchActions';
import colors from '@/theme/constants/colors';
import constantsFactory from '@/utils/constants';
import {
  StyledDesktopNavigation,
  StyledDivider,
  StyledListItem,
  StyledMainElements,
  StyledMegaMenu,
  StyledNavigation,
  StyledTopLink,
} from './styles';
import {Props} from './typings';

const {ROLES, DATA_TEST_ID} = constantsFactory();

const iconSize = 19;

const dashboardMenu = [
  {
    text: 'Artists',
    link: '/dashboard/artists/',
  },
  {
    text: 'Galleries',
    link: '/dashboard/galleries/',
  },
  {
    text: 'Stats',
    link: '/dashboard/stats/',
  },
  {
    text: 'Orders',
    link: '/dashboard/orders/',
  },
  {
    text: 'Users',
    link: '/dashboard/user/',
  },
  {
    text: 'Applications',
    link: '/dashboard/applications/',
  },
  {
    text: 'Accounting',
    link: '/dashboard/transactions/',
  },
  {
    text: 'Art of the day',
    link: '/dashboard/aotd/',
  },
  {
    text: 'Artist of the day',
    link: '/dashboard/artist-otd/',
  },
  {
    text: 'Artist homepage promos',
    link: '/dashboard/promotions/artist-hpp/',
  },
  {
    text: 'Marketing',
    link: '/dashboard/marketing/campaigns/',
  },
  {
    text: 'Problems',
    link: '/dashboard/problems/',
  },
  {
    text: 'Flags',
    link: '/dashboard/flags/',
  },
];

const DesktopNavigation = ({sharedNavigation, userSettings, auth}: Props) => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const menuItemsRef = useRef<(HTMLLIElement | null)[]>([]);

  /**
   * @description Handle keydown event for the navigation menu.
   * - If the Escape key is pressed and an item is active, focus the parent top-level link and close submenu.
   * @param {object} event KeyboardEvent
   * @return {undefined} void/procedure function
   */
  const handleNavKeyDown = (event: KeyboardEvent<HTMLUListElement>) => {
    if (event.key === 'Escape' && activeIndex !== null) {
      const parentLink: HTMLAnchorElement | null | undefined =
        menuItemsRef.current[activeIndex]?.querySelector('a[aria-haspopup="true"]');
      parentLink?.focus();
      setActiveIndex(null);
    }
  };

  /**
   * @description Handle keydown event for the top-level links.
   * - If the Space key is pressed, toggle the submenu open/close.
   * @param {object} event KeyboardEvent
   * @param {number} index index of the link
   * @return {undefined} void/procedure function
   */
  const handleLinkKeyDown = (event: KeyboardEvent<HTMLAnchorElement>, index: number) => {
    if (event.key === ' ') {
      event.preventDefault();
      setActiveIndex(activeIndex === index ? null : index);
    }
  };

  return (
    <StyledDesktopNavigation data-testid={DATA_TEST_ID.DESKTOP_NAVIGATION}>
      <StyledMainElements>
        <a aria-label="Go to Artfinder homepage" href={'/'}>
          <Logo width={146} height={74} color={colors.red100} />
        </a>

        <Search location="header" maxWidth="450px" searchAction={getSearchAutocomplete} />

        {auth.isAuthenticated() && auth.isAuthorised([ROLES.STAFF]) && (
          <span>
            <a href="/dashboard/" aria-label="Staff menu">
              <Icon type="settings" fontSize={iconSize} label={'Dashboard menu'} />
            </a>

            <ul>
              {dashboardMenu.map((el, i) => (
                <li key={i}>
                  <a href={el.link}>{el.text}</a>
                </li>
              ))}
            </ul>
          </span>
        )}

        {auth.isAuthenticated() && auth.isAuthorised([ROLES.PROVIDER]) && (
          <a href="/manage/" aria-label="Manage your artworks">
            <Icon type="dashboard" fontSize={iconSize} />
          </a>
        )}

        <ProfileMenu auth={auth} userSettings={userSettings} />

        <a href="/basket/" aria-label="Go to basket">
          <NotificationWrapper count={userSettings.basket_count}>
            <Icon type="trolley-empty" fontSize={iconSize} />
          </NotificationWrapper>
        </a>
      </StyledMainElements>

      <StyledNavigation>
        <ul onKeyDown={handleNavKeyDown}>
          {sharedNavigation.map((navElement, i) => {
            const isActive = activeIndex === i;
            return (
              <li
                key={i}
                onMouseEnter={() => setActiveIndex(i)}
                onMouseLeave={() => setActiveIndex(null)}
                ref={(el) => {
                  menuItemsRef.current[i] = el;
                }}
              >
                <StyledTopLink
                  href={navElement.link}
                  aria-haspopup={!!navElement.submenu}
                  aria-expanded={isActive}
                  tabIndex={0}
                  onKeyDown={(e) => handleLinkKeyDown(e, i)}
                  onFocus={() => setActiveIndex(null)}
                  $hasArrow={!!navElement.submenu && isActive}
                >
                  {navElement.text}
                </StyledTopLink>

                {navElement.submenu && (
                  <StyledMegaMenu role="menu" $isActive={isActive}>
                    <div>
                      {navElement.submenu.map((group, groupIndex) => (
                        <figure key={groupIndex}>
                          <figcaption>{group.title ?? ''}</figcaption>

                          <ul>
                            {group.links.map((submenuElement, i) => {
                              const content = submenuElement.component ?? submenuElement.text;

                              return (
                                <StyledListItem key={i}>
                                  {submenuElement.link ? (
                                    <a href={submenuElement.link} tabIndex={isActive ? 0 : -1}>
                                      {content}
                                    </a>
                                  ) : (
                                    content
                                  )}
                                </StyledListItem>
                              );
                            })}
                            {!!group.highlights?.length && (
                              <>
                                <StyledDivider />

                                {group.highlights.map((highlight, i) => {
                                  const content = highlight.component ?? highlight.text;

                                  return (
                                    <StyledListItem key={i} $bold>
                                      {highlight.link ? (
                                        <a href={highlight.link} tabIndex={isActive ? 0 : -1}>
                                          {content}
                                        </a>
                                      ) : (
                                        content
                                      )}
                                    </StyledListItem>
                                  );
                                })}
                              </>
                            )}
                          </ul>
                        </figure>
                      ))}
                    </div>
                  </StyledMegaMenu>
                )}
              </li>
            );
          })}
        </ul>
      </StyledNavigation>
    </StyledDesktopNavigation>
  );
};

export default DesktopNavigation;
